import { UseFormReturn } from "react-hook-form";
import { ListingFormValues } from "@/components/listings/filters/FilterSchema";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "@/components/ui/rich-text-editor"; // Import the new component
import { FileText } from "lucide-react";
import { useFormTouchDetection } from "@/hooks/useFormTouchDetection";

interface TitleDescriptionStepProps {
  form: UseFormReturn<ListingFormValues>;
}

const TitleDescriptionStep = ({ form }: TitleDescriptionStepProps) => {
  // Use the custom hook to handle form touch detection
  useFormTouchDetection(".title-description-form");

  // Disable automatic validation on focus
  const originalTrigger = form.trigger;
  form.trigger = async (name?: any, options?: any) => {
    // If trigger is called without explicit options, ensure we don't focus
    if (!options) {
      options = { shouldFocus: false };
    }
    return originalTrigger(name, options);
  };

  return (
    <div className="space-y-6 title-description-form">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-propertylk-blue" />
            Title & Description
          </CardTitle>
          <CardDescription>
            Provide a compelling title and detailed description for your
            property
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Property Title <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Modern Office Space in Colombo"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cplk_ref"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CPLK Reference Code</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., CPLK-12345"
                    {...field}
                    value={field.value || ""} // Ensure controlled component
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Description <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <RichTextEditor
                    value={field.value || ""}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default TitleDescriptionStep;
